/**
 * useChatStream — SSE streaming hook for AI chat
 *
 * Handles:
 *   - Loading message history from API
 *   - Sending user messages (optimistic UI update)
 *   - Streaming AI replies via SSE from /api/v1/chat/:id/stream
 *   - File upload via presigned R2 URL
 */

import { useState, useCallback, useRef } from 'react';
import { api, getToken, BASE } from '@/lib/api';
import { useChatContextStore } from '@/stores/chat-context-store';
import { resolveDynamicRuntimeChatContext } from '@/lib/dynamic-runtime-context';

// ─── Types ─────────────────────────────────────────

export interface LayerInvocationEnvelope {
  envelopeId: string;
  tenantId: string;
  runId: string;
  goalId?: string;
  conversationId?: string;
  intent: string;
  taskCategory: {
    category: string;
    confidence: number;
    matchedSignals?: string[];
    requiresApproval?: boolean;
    requiresParallelReview?: boolean;
  };
  context: {
    promptContext: {
      taskCategory: string;
      promptProfile: string;
      contextBlocks: Array<{
        kind: string;
        title: string;
        priority?: number;
        tokenBudgetHint?: number;
        sourceRef?: string | null;
      }>;
      notes?: string[];
    };
    precedentRefs?: Array<{
      refId: string;
      title?: string;
      source?: string;
      score?: number;
    }>;
  };
  intelligence: {
    routeRole?: string;
    agentKey?: string;
    model?: string;
    skillIds?: string[];
    toolIds?: string[];
    outputContract?: string;
  };
  harness: {
    phase: string;
    executionMode?: string;
    riskClass: string;
    replayable?: boolean;
    auditRequired: boolean;
    councilVerdict?: {
      taskCategory?: string;
      finalStance?: string;
      confidence?: number;
      consensusLevel?: string;
      recommendedAction?: string;
      reasons?: string[];
      opinions?: Array<{
        memberId: string;
        stance: string;
        confidence: number;
        summary: string;
        risks?: string[];
        evidenceRefs?: string[];
      }>;
    };
    approvalRecommendation?: {
      recommended?: boolean;
    };
  };
  replay?: {
    replayOfRunId?: string;
    baselineEnvelopeId?: string;
    comparisonMode?: string;
    expectedOutputSummary?: string;
  };
  createdAt: string;
}

export const CHAT_REPLY_LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: '简体中文' },
  { value: 'zh-Hant', label: '繁體中文' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'ja', label: '日本語' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
] as const;

export type ChatReplyLanguage = typeof CHAT_REPLY_LANGUAGE_OPTIONS[number]['value'];

export interface ThinkingStep {
  type: 'thinking' | 'tool_call' | 'tool_result';
  text: string;
  toolName?: string;
  isError?: boolean;
  timestamp?: string;
}

export interface AgentInfo {
  agentKey: string;
  templateSlug: string;
  name: string;
  nameZh?: string;
  role: string;
  department: string;
}

export interface ChatActionResult {
  actionType: string;
  status: string;
  jobId?: string;
  jobNumber?: string;
  attachmentIds?: string[];
  /** R2 storage key of the source PDF/image — used by HmsJobAuditModal */
  attachmentKey?: string | null;
  /** MIME type of the source attachment */
  attachmentMimeType?: string | null;
  navigationHint?: {
    module: string;
    path: string;
    pageKey: string;
    jobId?: string;
    jobNumber?: string;
  } | null;
  extracted?: Record<string, string> | null;
  missingFields?: string[];
}

/** Autonomous mode evaluation result from a single round */
export interface AutonomousEvaluation {
  round: number;
  isComplete: boolean;
  confidence: number;
  reflection: string;
  suggestedAction: 'continue' | 'escalate' | 'complete';
}

/** Autonomous execution state attached to streaming messages */
export interface AutonomousState {
  active: boolean;
  goalDescription?: string;
  maxRounds?: number;
  currentRound?: number;
  evaluations: AutonomousEvaluation[];
  completed?: boolean;
  totalRounds?: number;
  error?: string;
}

export interface ApprovalRecommendation {
  taskCategory: string;
  recommended: boolean;
  reason: string;
  recommendedAction: string;
  urgency: 'low' | 'medium' | 'high';
  finalStance: 'approve' | 'revise' | 'reject' | 'escalate';
  confidence: number;
  consensusLevel: 'low' | 'medium' | 'high';
  risks: string[];
  reasons: string[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  attachmentKeys?: string[];
  /** If content is JSON with a "type" field, it's a widget */
  widgetData?: ChatWidget;
  /** Thinking/reasoning steps shown during streaming, collapsible after */
  thinkingSteps?: ThinkingStep[];
  /** AI employee identity — which agent handled this message */
  agentInfo?: AgentInfo;
  /** Autonomous execution state — present when agent enters multi-round mode */
  autonomousState?: AutonomousState;
  /** Approval recommendation from the harness / council layer */
  approvalRecommendation?: ApprovalRecommendation;
  /** Gen5 Context / Intelligence / Harness invocation envelope */
  layerInvocationEnvelope?: LayerInvocationEnvelope;
}

export interface ChatWidget {
  type: 'approval' | 'table' | 'form' | 'action_form' | 'chart' | 'list' | 'info';
  title?: string;
  [key: string]: unknown;
}

export interface ChatSession {
  id: string;
  subject: string;
  status: string;
  created_at: string;
  last_message_at?: string;
}

// ─── Widget detection ────────────────────────────

// Re-exported from shared helpers for backward compat
export { tryParseWidget, dbMsgToChat, mergeSyncedMessages } from './useChatStreamHelpers';
import { tryParseWidget, dbMsgToChat, mergeSyncedMessages } from './useChatStreamHelpers';

// ─── Hook ─────────────────────────────────────────

export type ChatMode = 'brain' | 'basic' | null;

export function useChatStream(convId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ChatMode>(null);
  const streamingIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /** Load message history for current conversation */
  const loadMessages = useCallback(async () => {
    if (!convId) { setMessages([]); return; }
    setLoading(true);
    try {
      const res = await api.get<{ items: Record<string, unknown>[] }>(`/chat/${convId}/messages?limit=100`);
      setMessages((res.items ?? []).map(dbMsgToChat));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [convId]);

  /** Send a user message; streams AI reply token-by-token */
  const sendMessage = useCallback(async (
    content: string,
    opts: { agentId?: string; attachmentKeys?: string[]; targetConvId?: string; responseLanguage?: ChatReplyLanguage } = {},
  ) => {
    const cid = opts.targetConvId ?? convId;
    if (!cid || streaming) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
      attachmentKeys: opts.attachmentKeys,
    };
    setMessages(prev => [...prev, userMsg]);

    // Placeholder for streaming assistant message
    const streamId = `stream-${Date.now()}`;
    streamingIdRef.current = streamId;
    const assistantPlaceholder: ChatMessage = {
      id: streamId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, assistantPlaceholder]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    let accText = '';

    try {
      const chatContextState = useChatContextStore.getState();
      const dynamicRuntimeContext = await resolveDynamicRuntimeChatContext({
        pageContext: chatContextState.pageContext,
        question: content,
      });

      const response = await fetch(`${BASE}/chat/${cid}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          content,
          agentId: opts.agentId,
          attachmentKeys: opts.attachmentKeys ?? [],
          responseLanguage: opts.responseLanguage ?? 'auto',
          chatSpace: chatContextState.activeSpace,
          pageContext: chatContextState.pageContext,
          dynamicRuntimeContext,
        }),
      });

      if (!response.ok || !response.body) {
        const status = response.status;
        const msg = status === 401 ? 'Session expired — please sign in again'
          : status === 404 ? 'Chat session not found'
          : `Server error (${status})`;
        throw new Error(msg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accThinking = '';
      const accSteps: ThinkingStep[] = [];
      let accAutonomous: AutonomousState | undefined;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          try {
            const evt = JSON.parse(raw) as {
              type: string;
              text?: string;
              mode?: string;
              error?: string;
              toolName?: string;
              toolCallId?: string;
              isError?: boolean;
            };
            if (evt.type === 'mode' && evt.mode) {
              setMode(evt.mode as ChatMode);
            } else if (evt.type === 'text' && evt.text) {
              accText += evt.text;
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId
                    ? { ...m, content: accText, widgetData: tryParseWidget(accText), thinkingSteps: accSteps.length > 0 ? [...accSteps] : undefined }
                    : m,
                ),
              );
            } else if (evt.type === 'thinking' && evt.text) {
              accThinking += evt.text;
              // Update or append streaming thinking step
              const existingIdx = accSteps.findIndex(s => s.type === 'thinking' && !s.toolName);
              if (existingIdx >= 0) {
                accSteps[existingIdx] = { ...accSteps[existingIdx], type: 'thinking' as const, text: accThinking };
              } else {
                accSteps.push({ type: 'thinking', text: accThinking, timestamp: new Date().toISOString() });
              }
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId
                    ? { ...m, thinkingSteps: [...accSteps] }
                    : m,
                ),
              );
            } else if (evt.type === 'tool_call') {
              accThinking = ''; // reset for next thinking block
              accSteps.push({
                type: 'tool_call',
                text: `Calling ${evt.toolName ?? 'tool'}…`,
                toolName: evt.toolName,
                timestamp: new Date().toISOString(),
              });
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId
                    ? { ...m, thinkingSteps: [...accSteps] }
                    : m,
                ),
              );
            } else if (evt.type === 'tool_result') {
              accSteps.push({
                type: 'tool_result',
                text: evt.isError ? `${evt.toolName ?? 'tool'} failed` : `${evt.toolName ?? 'tool'} done`,
                toolName: evt.toolName,
                isError: evt.isError,
                timestamp: new Date().toISOString(),
              });
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId
                    ? { ...m, thinkingSteps: [...accSteps] }
                    : m,
                ),
              );
            } else if (evt.type === 'agent_info') {
              const info: AgentInfo = {
                agentKey: (evt as Record<string, unknown>).agentKey as string ?? '',
                templateSlug: (evt as Record<string, unknown>).templateSlug as string ?? '',
                name: (evt as Record<string, unknown>).name as string ?? '',
                nameZh: (evt as Record<string, unknown>).nameZh as string | undefined,
                role: (evt as Record<string, unknown>).role as string ?? '',
                department: (evt as Record<string, unknown>).department as string ?? '',
              };
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, agentInfo: info } : m,
                ),
              );
            } else if (evt.type === 'layer_invocation_envelope') {
              const envelope = (evt as Record<string, unknown>).envelope as LayerInvocationEnvelope | undefined;
              if (envelope) {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === streamId ? { ...m, layerInvocationEnvelope: envelope } : m,
                  ),
                );
              }
            } else if (evt.type === 'approval_recommendation') {
              const recommendation = (evt as Record<string, unknown>).recommendation as ApprovalRecommendation | undefined;
              if (recommendation) {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === streamId ? { ...m, approvalRecommendation: recommendation } : m,
                  ),
                );
              }
            } else if (evt.type === 'action_result') {
              const actionResult: ChatActionResult = {
                actionType: (evt as Record<string, unknown>).actionType as string ?? '',
                status: (evt as Record<string, unknown>).status as string ?? '',
                jobId: (evt as Record<string, unknown>).jobId as string | undefined,
                jobNumber: (evt as Record<string, unknown>).jobNumber as string | undefined,
                attachmentIds: ((evt as Record<string, unknown>).attachmentIds as string[] | undefined) ?? [],
                attachmentKey: ((evt as Record<string, unknown>).attachmentKey as string | null | undefined) ?? null,
                attachmentMimeType: ((evt as Record<string, unknown>).attachmentMimeType as string | null | undefined) ?? null,
                navigationHint: ((evt as Record<string, unknown>).navigationHint as ChatActionResult['navigationHint']) ?? null,
                extracted: ((evt as Record<string, unknown>).extracted as Record<string, string> | undefined) ?? null,
                missingFields: ((evt as Record<string, unknown>).missingFields as string[] | undefined) ?? [],
              };
              window.dispatchEvent(new CustomEvent('boox:chat-action', { detail: actionResult }));
            } else if (evt.type === 'autonomous_start') {
              // Agent entered autonomous multi-round execution mode
              const e = evt as Record<string, unknown>;
              accAutonomous = {
                active: true,
                goalDescription: e.goalDescription as string ?? '',
                maxRounds: e.maxRounds as number ?? 3,
                currentRound: 0,
                evaluations: [],
              };
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, autonomousState: { ...accAutonomous! } } : m,
                ),
              );
            } else if (evt.type === 'autonomous_round' && accAutonomous) {
              const e = evt as Record<string, unknown>;
              accAutonomous = {
                ...accAutonomous,
                currentRound: e.round as number ?? accAutonomous.currentRound,
              };
              // Reset thinking accumulator for new round
              accThinking = '';
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, autonomousState: { ...accAutonomous! } } : m,
                ),
              );
            } else if (evt.type === 'autonomous_eval' && accAutonomous) {
              const e = evt as Record<string, unknown>;
              const evaluation: AutonomousEvaluation = {
                round: e.round as number ?? 0,
                isComplete: e.isComplete as boolean ?? false,
                confidence: e.confidence as number ?? 0,
                reflection: e.reflection as string ?? '',
                suggestedAction: (e.suggestedAction as AutonomousEvaluation['suggestedAction']) ?? 'continue',
              };
              accAutonomous = {
                ...accAutonomous,
                evaluations: [...accAutonomous.evaluations, evaluation],
              };
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, autonomousState: { ...accAutonomous! } } : m,
                ),
              );
            } else if (evt.type === 'autonomous_complete' && accAutonomous) {
              const e = evt as Record<string, unknown>;
              accAutonomous = {
                ...accAutonomous,
                active: false,
                completed: e.completed as boolean ?? true,
                totalRounds: e.rounds as number ?? accAutonomous.currentRound,
              };
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, autonomousState: { ...accAutonomous! } } : m,
                ),
              );
            } else if (evt.type === 'autonomous_error' && accAutonomous) {
              const e = evt as Record<string, unknown>;
              accAutonomous = {
                ...accAutonomous,
                active: false,
                error: e.error as string ?? 'Unknown error',
              };
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, autonomousState: { ...accAutonomous! } } : m,
                ),
              );
            } else if (evt.type === 'error' && evt.error) {
              accText += `⚠️ ${evt.error}`;
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamId ? { ...m, content: accText } : m,
                ),
              );
            }
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      // If aborted by user, keep accumulated text and remove thinking steps
      if (controller.signal.aborted) {
        setMessages(prev =>
          prev.map(m =>
            m.id === streamId
              ? {
                ...m,
                thinkingSteps: undefined,
                autonomousState: m.autonomousState
                  ? { ...m.autonomousState, active: false }
                  : undefined,
              }
              : m,
          ),
        );
      } else {
        // Replace placeholder with error message
        setMessages(prev =>
          prev.map(m =>
            m.id === streamId
              ? {
                ...m,
                content: `⚠️ ${err instanceof Error ? err.message : 'Connection error'}`,
                autonomousState: m.autonomousState
                  ? {
                    ...m.autonomousState,
                    active: false,
                    error: err instanceof Error ? err.message : 'Connection error',
                  }
                  : undefined,
              }
              : m,
          ),
        );
      }
    } finally {
      setStreaming(false);
      streamingIdRef.current = null;
      abortRef.current = null;
      // Sync messages from DB so temp-IDs become real and any server-saved reply is persisted
      try {
        if (cid) {
          const res = await api.get<{ items: Record<string, unknown>[] }>(`/chat/${cid}/messages?limit=100`);
          const dbMessages = (res.items ?? []).map(dbMsgToChat);
          if (dbMessages.length > 0) {
            setMessages(prev => mergeSyncedMessages(prev, dbMessages, streamId));
          }
        }
      } catch { /* best-effort sync */ }
    }

    return accText;
  }, [convId, streaming]);

  /** Upload a file and return its storage key */
  const uploadFile = useCallback(async (file: File): Promise<string> => {
    // Convert file to base64
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
    );

    // Upload via backend proxy (avoids R2 CORS issues)
    const res = await api.post<{ key: string }>('/upload/direct', {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      data: base64,
      entityType: 'chat',
    });

    return res.key;
  }, []);

  /** Cancel the in-flight stream */
  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { messages, streaming, loading, mode, loadMessages, sendMessage, uploadFile, cancelStream };
}
